import * as React from 'react';
import { useEffect } from 'react';

import SvgError from '@sats-group/ui-lib/react/icons/16/error';
import SvgInfo from '@sats-group/ui-lib/react/icons/16/info';
import Text from '@sats-group/ui-lib/react/text';
import useInputValidation from '@sats-group/ui-lib/react/use-input-validation';

import type { PhoneInputField as Props } from './phone-input-field.props';

const RefTextInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      customErrorMessages,
      defaultValue,
      hasError,
      helpText,
      name,
      onChange = () => {},
      placeholder,
      required,
      ...restProps
    },
    ref
  ) => {
    const [isError, setIsError] = React.useState(hasError);
    const [validationOnChange, onInvalid, error] = useInputValidation(
      customErrorMessages,
      customErrorMessages ? customErrorMessages.defaultError : undefined,
      isError
    );

    useEffect(() => {
      setIsError(hasError);
    }, [hasError]);

    return (
      <div className="phone-input-field">
        <div className="phone-input-field__input-wrapper">
          <input
            aria-label={name}
            className="phone-input-field__input"
            defaultValue={defaultValue}
            required={required}
            name={name}
            onInvalid={e => onInvalid(e)}
            onChange={e => {
              onChange(e);
              validationOnChange(e);
              setIsError(false); // NOTE: We want to reset error state on change to not confuse users.
            }}
            placeholder={placeholder}
            ref={ref}
            type="tel"
            {...restProps}
          />
        </div>
        {helpText ? (
          <div className="phone-input-field__help">
            <SvgInfo />
            <Text size={Text.sizes.interface}>{helpText}</Text>
          </div>
        ) : null}
        {/* NOTE: This is aria-hidden because reporting of validation errors is handled by the browser */}
        {error ? (
          <div aria-hidden="true" className="phone-input-field__error">
            <SvgError />
            <Text size={Text.sizes.interface}>{error}</Text>
          </div>
        ) : null}
      </div>
    );
  }
);

// NOTE: If this isn't set, stack traces say "forwardRef" instead of "TextInput"
RefTextInput.displayName = 'PhoneInputField';

const PhoneInputField: typeof RefTextInput = Object.assign(RefTextInput);

export default PhoneInputField;
